import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { format, isSameDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { MapBackLocationTypeIcon } from './MapBackLocationIcon';
import { TimelineItemUI } from '~/utils/interface/mapback';
import type { IPersonStatus } from '~/utils/interface/map';
import { getJourneySegmentTypeActiveBg, getJourneySegmentTypeColor } from '../constants/segmentTypeColors';
import { useSetHoveredSegmentIndex } from '../context/JourneyHighlightContext';
import { useJourneyPersonStatuses } from '../context/JourneyPersonStatusContext';
import styles from '../Journey.module.scss';

interface JourneyTimelineProps {
  timelineItems: TimelineItemUI[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  hasValidDateRange?: boolean;
  selectedDate?: Date | null;
  selectedSegmentIndex?: number | null;
  enableActiveHighlight?: boolean;
  onSegmentClick?: (segmentIndex: number) => void;
  onSegmentDoubleClick?: (segmentIndex: number) => void;
  isLoading: boolean;
}

interface JourneyTimelineRowProps {
  item: TimelineItemUI;
  originalIndex: number;
  isLast: boolean;
  isSelected: boolean;
  personStatuses: IPersonStatus[];
  suppressedHoverSegmentIndex: number | null;
  onHoverChange: (originalIndex: number | null) => void;
  onSegmentClick?: (segmentIndex: number) => void;
  onSegmentDoubleClick?: (segmentIndex: number) => void;
  onSuppressHover: (segmentIndex: number | null) => void;
  rowRef: (el: HTMLDivElement | null) => void;
  placeholder: string;
}

const JourneyTimelineRow = React.memo(function JourneyTimelineRow({
  item,
  originalIndex,
  isLast,
  isSelected,
  personStatuses,
  suppressedHoverSegmentIndex,
  onHoverChange,
  onSegmentClick,
  onSegmentDoubleClick,
  onSuppressHover,
  rowRef,
  placeholder,
}: JourneyTimelineRowProps) {
  const rowHoverBg = getJourneySegmentTypeActiveBg(item.type, personStatuses);
  const rowSelectedBorder = getJourneySegmentTypeColor(item.type, personStatuses);

  return (
    <Box
      component='div'
      ref={rowRef}
      className={`${styles.timelineRow} ${isSelected ? styles.timelineRowSelected : ''}`}
      style={
        {
          '--timeline-row-hover-bg': rowHoverBg,
          '--timeline-row-selected-bg': rowHoverBg,
          '--timeline-row-selected-border': rowSelectedBorder,
        } as React.CSSProperties
      }
      onMouseEnter={() => {
        if (suppressedHoverSegmentIndex === originalIndex) return;
        onHoverChange(originalIndex);
      }}
      onMouseLeave={() => {
        if (suppressedHoverSegmentIndex === originalIndex) {
          onSuppressHover(null);
        }
        onHoverChange(null);
      }}
      onClick={() => {
        onSuppressHover(null);
        onSegmentClick?.(originalIndex);
      }}
      onDoubleClick={() => {
        onSuppressHover(originalIndex);
        onHoverChange(null);
        onSegmentDoubleClick?.(originalIndex);
      }}
    >
      <Box className={styles.timelineColLeft}>
        <Typography component='p' className={styles.timelineStartTime}>
          {item.startTime}
        </Typography>
        <Typography component='p' className={styles.timelineStartDate}>
          {item.startDate}
        </Typography>
      </Box>

      <Box className={styles.timelineIconCell}>
        <Box className={styles.timelineIconTrack}>
          <Box component='span' className={styles.timelineIconWrap}>
            <MapBackLocationTypeIcon type={item.type} personStatuses={personStatuses} />
          </Box>
          {!isLast && <Box component='span' className={styles.timelineLine} />}
        </Box>
      </Box>

      <Box className={styles.timelineColMain}>
        <Typography component='p' className={styles.timelineDisplayValue}>
          {item.displayValue || placeholder}
        </Typography>
        <Typography component='p' className={styles.timelineDuration}>
          {item.duration}
        </Typography>
      </Box>

      <Box className={styles.timelineColRight}>
        <Box className={styles.timelineTimeRange}>
          <Typography component='span' style={{ fontSize: '14px' }}>
            {`${item.startTime} - ${item.endTime}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

const JourneyTimeline: React.FC<JourneyTimelineProps> = ({
  timelineItems,
  dateRange,
  hasValidDateRange = false,
  selectedDate,
  selectedSegmentIndex = null,
  enableActiveHighlight = false,
  onSegmentClick,
  onSegmentDoubleClick,
  isLoading,
}) => {
  const { t } = useTranslation('admin/journey');
  const personStatuses = useJourneyPersonStatuses();
  const setHoveredSegmentIndex = useSetHoveredSegmentIndex();
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [suppressedHoverSegmentIndex, setSuppressedHoverSegmentIndex] = useState<number | null>(null);
  const safeTimelineItems = useMemo(() => (Array.isArray(timelineItems) ? timelineItems : []), [timelineItems]);
  const selectedItemId =
    selectedSegmentIndex !== null ? (safeTimelineItems[selectedSegmentIndex]?.id ?? null) : null;

  const visibleTimelineItems = useMemo(() => {
    if (!selectedDate) {
      return safeTimelineItems.map((item, index) => ({ item, originalIndex: index }));
    }
    const selectedDateText = format(selectedDate, 'dd/MM/yyyy');
    return safeTimelineItems
      .map((item, index) => ({ item, originalIndex: index }))
      .filter(({ item }) => item.startDate === selectedDateText);
  }, [safeTimelineItems, selectedDate]);

  const selectedVisibleIndex = useMemo(() => {
    if (!selectedItemId) return null;
    const idx = visibleTimelineItems.findIndex(({ item }) => item.id === selectedItemId);
    return idx >= 0 ? idx : null;
  }, [selectedItemId, visibleTimelineItems]);

  const timelineHeaderDate = useMemo(() => {
    if (!hasValidDateRange || !dateRange.start || !dateRange.end) return '';
    if (selectedDate) return format(selectedDate, 'dd/MM/yyyy');
    const start = dateRange.start;
    const end = dateRange.end;
    if (isSameDay(start, end)) return format(start, 'dd/MM/yyyy');
    return `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
  }, [dateRange.end, dateRange.start, hasValidDateRange, selectedDate]);

  const handleRowHoverChange = useCallback(
    (originalIndex: number | null) => {
      setHoveredSegmentIndex(originalIndex);
    },
    [setHoveredSegmentIndex]
  );

  useEffect(() => {
    if (!enableActiveHighlight) return;
    if (selectedVisibleIndex === null) return;
    const target = itemRefs.current[selectedVisibleIndex];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedVisibleIndex, enableActiveHighlight]);

  return (
    <Box className={styles.timelineCard}>
      <Typography component='h3' className={styles.timelineHeader}>
        <Box component='span'>{timelineHeaderDate}</Box>
      </Typography>

      {isLoading ? (
        <Box className={styles.timelineLoading}>
          {[1, 2, 3, 4].map((idx) => (
            <Box key={idx} className={styles.timelineRowSkeleton}>
              <Box className='space-y-2'>
                <Box className='h-4 w-12 animate-pulse rounded bg-gray-200' />
                <Box className='h-4 w-16 animate-pulse rounded bg-gray-200' />
              </Box>
              <Box className={styles.timelineIconCell}>
                <Box className='mt-1 h-5 w-5 animate-pulse rounded-full bg-gray-200' />
              </Box>
              <Box className='flex-1 space-y-2 pt-1'>
                <Box className='h-4 w-4/5 animate-pulse rounded bg-gray-200' />
                <Box className='h-3 w-1/4 animate-pulse rounded bg-gray-200' />
              </Box>
              <Box className='space-y-2'>
                <Box className='h-4 w-full animate-pulse rounded bg-gray-200' />
                <Box className='h-3 w-3/4 animate-pulse rounded bg-gray-200' />
              </Box>
            </Box>
          ))}
        </Box>
      ) : !hasValidDateRange ? (
        <Typography component='p' className={styles.timelineEmpty}>
          {t('empty.selectDateRangePrompt')}
        </Typography>
      ) : visibleTimelineItems.length === 0 ? (
        <Typography component='p' className={styles.timelineEmpty}>
          {t('empty.noRouteData')}
        </Typography>
      ) : (
        <Box className={`${styles.timelineList} ${styles.timelineScroll}`}>
          {visibleTimelineItems.map((entry, idx) => (
            <JourneyTimelineRow
              key={entry.item.id}
              item={entry.item}
              originalIndex={entry.originalIndex}
              isLast={idx === visibleTimelineItems.length - 1}
              isSelected={enableActiveHighlight && entry.item.id === selectedItemId}
              personStatuses={personStatuses}
              suppressedHoverSegmentIndex={suppressedHoverSegmentIndex}
              onHoverChange={handleRowHoverChange}
              onSegmentClick={onSegmentClick}
              onSegmentDoubleClick={onSegmentDoubleClick}
              onSuppressHover={setSuppressedHoverSegmentIndex}
              rowRef={(el) => {
                itemRefs.current[idx] = el;
              }}
              placeholder={t('empty.placeholder')}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(JourneyTimeline);
